import clsx from 'clsx';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { ResizableLayoutDirection } from '../types';

import { Icon } from '../../Icon/Icon';
import './ResizeIndicator.css';

const messages = defineMessages({
  resizeWidth: {
    id: 'ResizableLayout.resizeWidth',
    defaultMessage: 'Resize width',
    description: 'Accessible name for the horizontal resize handle',
  },
  resizeHeight: {
    id: 'ResizableLayout.resizeHeight',
    defaultMessage: 'Resize height',
    description: 'Accessible name for the vertical resize handle',
  },
  handleTitle: {
    id: 'ResizableLayout.handleTitle',
    defaultMessage: 'Drag to resize · Double-click to reset',
    description: 'Tooltip for the resize handle',
  },
});

type ResizeIndicatorProps = {
  ariaValueMin: number;
  ariaValueNow: number;
  direction: ResizableLayoutDirection;
  isDragging: boolean;
  onDoubleClick: () => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLButtonElement>) => void;
  onLostPointerCapture: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  ariaValueMax?: number;
  label?: string;
  __private?: {
    hideIndicator?: boolean;
  };
};

export const ResizeIndicator = ({
  ariaValueMax,
  ariaValueMin,
  ariaValueNow,
  direction,
  isDragging,
  label,
  onDoubleClick,
  onKeyDown,
  onLostPointerCapture,
  onPointerCancel,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  __private,
}: ResizeIndicatorProps) => {
  const intl = useIntl();
  const isWidthAxis = direction === 'left' || direction === 'right';

  return (
    <button
      aria-label={
        label ??
        intl.formatMessage(
          isWidthAxis ? messages.resizeWidth : messages.resizeHeight,
        )
      }
      aria-orientation={isWidthAxis ? 'vertical' : 'horizontal'}
      aria-valuemax={ariaValueMax}
      aria-valuemin={ariaValueMin}
      aria-valuenow={ariaValueNow}
      role="separator"
      className={clsx(
        'eds-resizable-layout--handle',
        isWidthAxis
          ? 'eds-resizable-layout--handle--vertical'
          : 'eds-resizable-layout--handle--horizontal',
        {
          'eds-resizable-layout--handle--active': isDragging,
          'eds-resizable-layout--handle--hidden-indicator':
            __private?.hideIndicator,
        },
      )}
      tabIndex={0}
      title={intl.formatMessage(messages.handleTitle)}
      type="button"
      onDoubleClick={onDoubleClick}
      onKeyDown={onKeyDown}
      onLostPointerCapture={onLostPointerCapture}
      onPointerCancel={onPointerCancel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <span
        aria-hidden="true"
        className={clsx(
          'eds-resizable-layout--indicator',
          isWidthAxis
            ? 'eds-resizable-layout--indicator--vertical'
            : 'eds-resizable-layout--indicator--horizontal',
          __private?.hideIndicator && 'eds-resizable-layout--indicator--hidden',
        )}
      >
        <span
          className={clsx(
            'eds-resizable-layout--glyph',
            !isWidthAxis && 'eds-resizable-layout--glyph--horizontal',
          )}
        >
          <Icon icon="drag-and-drop-indicator" size="s" />
        </span>
      </span>
    </button>
  );
};
